/**
 * The reviewer's notes on one suggestion (draft page redesign): "Why this is
 * held (N)", collapsed, under the suggestion's review strip. The notes sit
 * next to the text they are about, not in the document overview's recap.
 * Renders nothing when there are no notes.
 *
 * `JobSuggestionReviewNotes` feeds it from the document's latest job: the
 * notes are a snapshot from when the job ended, shown only with a suggestion
 * that job left (`reviewNotesForSuggestion`). The strip itself is only on
 * screen while its suggestion is pending.
 */

import { useEffect, useRef } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  reviewNotesForSuggestion,
  type SuggestionForNotes,
  type SuggestionNoteLine,
} from '@/lib/document-job-progress'
import { useActiveJobsStore } from '@/stores/active-jobs-store'

function notesLabel(count: number): string {
  return `Why this is held (${count})`
}

function moreLine(count: number): string {
  return `+ ${count} more ${count === 1 ? 'note' : 'notes'} not shown.`
}

interface SuggestionReviewNotesProps {
  notes: ReadonlyArray<SuggestionNoteLine>
  /** Notes left out of the list; ends it with "+ N more notes not shown." */
  more?: number
  className?: string | undefined
}

export function SuggestionReviewNotes({ notes, more = 0, className }: SuggestionReviewNotesProps) {
  if (notes.length === 0) return null
  return (
    <details className={cn('group shrink-0 border-b bg-muted/20', className)} data-testid="suggestion-review-notes">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-1.5 text-meta [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-subtle transition-transform group-open:rotate-90" aria-hidden />
        {notesLabel(notes.length)}
      </summary>
      <ul className="list-disc space-y-1 pb-2 pl-10 pr-3 text-sm">
        {notes.map((note, index) => (
          <li key={`${index}-${note.text}`}>
            {note.label && <span className="text-meta mr-1.5" data-testid="review-note-label">{note.label}</span>}
            {note.text}
          </li>
        ))}
        {more > 0 && <li className="list-none text-meta">{moreLine(more)}</li>}
      </ul>
    </details>
  )
}

interface JobSuggestionReviewNotesProps {
  artifactId: string
  suggestion: SuggestionForNotes & { proposalId: string }
  className?: string
}

/** The notes for the suggestion on screen, from the document's latest job. */
export function JobSuggestionReviewNotes({ artifactId, suggestion, className }: JobSuggestionReviewNotesProps) {
  const job = useActiveJobsStore((s) => s.getOverviewDocumentJob(artifactId))
  const loadLatestDocumentJob = useActiveJobsStore((s) => s.loadLatestDocumentJob)
  const isJobSuggestion = suggestion.mode === 'staged' && suggestion.turnId === null
  // A job older than the tray's window is not in its list: read the document's latest once per suggestion.
  const askedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!isJobSuggestion || askedFor.current === suggestion.proposalId) return
    askedFor.current = suggestion.proposalId
    void loadLatestDocumentJob(artifactId)
  }, [isJobSuggestion, suggestion.proposalId, artifactId, loadLatestDocumentJob])

  const { notes, more } = reviewNotesForSuggestion(job, suggestion)
  return <SuggestionReviewNotes notes={notes} more={more} className={className} />
}
